import jsQR from 'jsqr';
import { LitElement } from '~/lib/bn-lit-element';

import './bn-loading';
import './bn-flash';
import { html, css, renderIf } from '../utility/lit-entity';
import { sharedStyles } from '../styles/shared-styles';

const HIGHLIGHT_COLOUR = '#FF3B58';
const THROTTLE_QR = 5;
const LOCATION_MARKER_TIMEOUT_MS = 1200;

class QrScanner extends LitElement {
  constructor() {
    super();
    this.qrData = '';
    this.loading = false;
    this.hideControls = false;
  }

  static get properties() {
    return {
      qrData: { type: String },
      loading: { type: Boolean },
      scanning: { type: Boolean },
      hideControls: { type: Boolean, attribute: 'hide-controls' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        box-sizing: border-box;
      }
      .actions {
        text-align: center;
        margin-bottom: 0.5rem;
        padding: 0.5rem;
      }
      .preview {
        text-align: center;
        margin: var(--scanner-margin, 1em auto 0);
        background-color: var(--gray-40);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
      bn-loading {
        margin: 1em;
        padding: 0 2em;
        box-shadow: 0 0 3px var(--gray-50);
        background-color: var(--surface);
        text-align: center;
      }
      canvas {
        max-width: 100%;
        max-height: 400px;
      }
      .qr-data {
        font-size: .8em;
        color: var(--gray-70);
        margin-bottom: 1em;
      }
    `];
  }

  render() {
    return html`
      ${renderIf(!this.hideControls, () => html`
        <div class="actions well">
          <button class="button button--contained" @click="${this._start}" ?disabled="${this.scanning}">Start</button>
          <button class="button button--outlined" @click="${this._stop}" ?disabled="${!this.scanning}">Stop</button>
        </div>
      `)}
      <bn-flash></bn-flash>
      <div class="preview">
        ${renderIf(this.loading, html`<bn-loading><p>Attempting to Access Camera</p></bn-loading>`)}
        <canvas height="0" width="0"></canvas>
      </div>
      <div class="qr-data">${this.qrData}</div>
    `;
  }

  drawLine(begin, end, color) {
    const { canvasContext } = this;
    canvasContext.beginPath();
    canvasContext.moveTo(begin.x, begin.y);
    canvasContext.lineTo(end.x, end.y);
    canvasContext.lineWidth = 4;
    canvasContext.strokeStyle = color;
    canvasContext.stroke();
  }

  firstUpdated() {
    if (this.hideControls && !this.scanning) {
      this._start();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stop();
  }

  async _start() {
    this.scanning = true;
    if (this.video) {
      this.video.play();
      return;
    }
    try {
      this.loading = true;
      this.video = document.createElement('video');
      this.canvasElement = this.shadowRoot.querySelector('canvas');
      this.canvasContext = this.canvasElement.getContext('2d');
      const deviceId = await this._getCompatibleDeviceId();
      const params = deviceId ? { video: { deviceId: { exact: deviceId } } } : { video: { facingMode: 'environment' } };
      this.video.srcObject = await navigator.mediaDevices.getUserMedia(params);
      this.video.setAttribute('playsinline', true); // required to tell iOS safari we don't want fullscreen
      this.video.play();
      this.frame = 0;
      window.requestAnimationFrame(this._tick.bind(this));
      this.dispatchEvent(new CustomEvent('start'));
    } catch (error) {
      this.loading = false;
      this.scanning = false;
      this.video = null;
      this.dispatchEvent(new CustomEvent('error', { detail: { error } }));
    }
  }

  _stop() {
    this.qrData = '';
    this.scanning = false;
    if (!this.video) {
      return;
    }
    this.canvasContext.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    this.canvasElement.height = 0;
    this.video.srcObject.getTracks().forEach((track) => track.stop());
    this.video = null;
    this.dispatchEvent(new CustomEvent('stop'));
  }

  async _tick() {
    const { video } = this;
    if (!video) {
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      this.drawImageData();

      this.frame = (this.frame + 1) % THROTTLE_QR;
      if (this.frame === 0) {
        this.forceFocusCamera();
        this.parseQRCode();
      }
      this.highlightQRCode();
    }
    window.requestAnimationFrame(this._tick.bind(this));
  }

  // this is a hack for certain devices with many cameras ie. Galaxy S10 plus
  async _getCompatibleDeviceId() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    // there must be exactly 4 video devices
    if (devices.filter((device) => device.kind === 'videoinput').length !== 4) {
      return;
    }

    // we want the device on the back numbered 0
    for (let i = 0; i < devices.length; i++) {
      const device = devices[i];
      if (device.kind === 'videoinput' && device.label.includes('0,') && device.label.includes('back')) {
        return device.deviceId;
      }
    }

    return;
  }

  drawImageData() {
    const { canvasElement, canvasContext, video } = this;
    this.loading = false;
    canvasElement.height = video.videoHeight;
    canvasElement.width = video.videoWidth;
    canvasContext.drawImage(video, 0, 0, canvasElement.width, canvasElement.height);
  }

  forceFocusCamera() {
    const videoTrack = this.video.srcObject.getVideoTracks()[0];

    if (typeof videoTrack.getCapabilities === 'function' && videoTrack.getCapabilities().focusMode && videoTrack.getCapabilities().focusMode.includes('single-shot')) {
      videoTrack.applyConstraints({ advanced: [{ focusMode: 'single-shot' }] });
    }
  }

  highlightQRCode() {
    if (!this.location) {
      return;
    }
    const { location } = this;
    this.drawLine(location.topLeftCorner, location.topRightCorner, HIGHLIGHT_COLOUR);
    this.drawLine(location.topRightCorner, location.bottomRightCorner, HIGHLIGHT_COLOUR);
    this.drawLine(location.bottomRightCorner, location.bottomLeftCorner, HIGHLIGHT_COLOUR);
    this.drawLine(location.bottomLeftCorner, location.topLeftCorner, HIGHLIGHT_COLOUR);
  }

  parseQRCode() {
    const { canvasContext, canvasElement } = this;
    const imageData = canvasContext.getImageData(0, 0, canvasElement.width, canvasElement.height);

    const code = jsQR(imageData.data, imageData.width, imageData.height);
    const detail = { data: undefined };
    if (code) {
      const { data, location } = code;
      this.location = location;
      clearTimeout(this.locationTimeout);
      this.locationTimeout = setTimeout(() => {
        this.location = null;
      }, LOCATION_MARKER_TIMEOUT_MS);
      detail.data = data;
      this.qrData = data;
    }
    this.dispatchEvent(new CustomEvent('data', { detail }));
  }
}

window.customElements.define('qr-scanner', QrScanner);
